import React, { useState, useEffect, useRef } from 'react';
import './../testimonials.scss';
import testimonialData from './../data/testimonialData';
import { useReducedMotion } from '../hooks/useReducedMotion';

const MemberTestimonials = () => {
    const [prefersReducedMotion] = useReducedMotion();
    const [selectedTestimonial, setSelectedTestimonial] = useState({});
    const [previousSelectedTestimonial, setPreviousSelectedTestimonial] = useState({});
    const [previousTestimonialImagePath, setPreviousTestimonialImagePath] = useState();
    const [testimonialSet, setTestimonialSet] = useState();
    const [testimonialImagePath, setTestimonialImagePath] = useState();
    const [stateImagePath, setStateImagePath] = useState();
    const [fadeIn, setFadeIn] = useState(true);
    const [fadeOutPrevious, setFadeOutPrevious] = useState(false);
    const [imgRect, setImgRect] = useState();
    const lineRef = useRef(null);
    const selectedImgRef = useRef(null);
    const [imagesLoaded, setImagesLoaded] = useState();
    const imageRefSet = useRef(new Array())
    const [initialRender, setInitialRender] = useState(false);
    const fadeTimeoutRef = useRef(null);

    // Guard against a stale fade timer from an earlier click overwriting a more recent selection:
    // clear any in-flight fade timeout on unmount so it never fires against an unmounted component.
    useEffect(() => {
        return () => {
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        testimonialData.forEach((testimonial) => {
            import(`./../assets/testimonials/${testimonial.Image}`).then((module) => {
                let imageUrl = module.default;
                let imagePath = (imageUrl);
                let index = testimonialData.indexOf(testimonial);
                let current = testimonialData[index];
                current["ImagePath"] = imagePath;

                if (index === testimonialData.length - 1) {
                    setImagesLoaded(true);
                }
            });
        });

        const firstOrderTestimonial = testimonialData.filter(x => x.Order === 1);

        if (firstOrderTestimonial.length > 0) {
            setSelectedTestimonial(firstOrderTestimonial[0]);
        }
    }, []);

    useEffect(() => {
        if (imageRefSet.current.length === 5) {

            setTimeout(() => {
                selectedImgRef.current = imageRefSet.current[0];

                const imagePosition = selectedImgRef?.current?.getBoundingClientRect();
                if (imagePosition) {
                    updateLineLocation(imagePosition);
                }
            }, 1000);
        }
    }, [imageRefSet]);

    useEffect(() => {
        if (selectedTestimonial?.Image) {
            // Dynamically import the image file
            import(`./../assets/testimonials/${selectedTestimonial.Image}`).then((module) => {
                const imageUrl = module.default;
                setTestimonialImagePath(imageUrl);
                setPreviousTestimonialImagePath(imageUrl);
            });

            if (selectedTestimonial?.State) {
                import(`./../assets/testimonials/map_icons (svg)/${selectedTestimonial.StateImage}`).then((module) => {
                    const stateImageUrl = module.default;
                    setStateImagePath(stateImageUrl);
                });
            }
        }
    }, [selectedTestimonial]);

    useEffect(() => {
        if (imgRect) {
            updateLineLocation();
        }
    }, [imgRect]);

    const handleOnImageClick = (testimonial, i, selectedImgRef) => {
        setPreviousSelectedTestimonial(selectedTestimonial);
        const selectedTestimonialImage = testimonialData.filter(x => x.Order === testimonial.Order);
        selectedImgRef.current = imageRefSet.current[i];

        const imagePosition = selectedImgRef.current.getBoundingClientRect();
        setImgRect(imagePosition);

        // Cancel any in-flight fade timer from a previous click so it can't fire later and
        // stomp this (more recent) selection with stale data.
        if (fadeTimeoutRef.current) {
            clearTimeout(fadeTimeoutRef.current);
            fadeTimeoutRef.current = null;
        }

        if (prefersReducedMotion) {
            // Skip the fade-out/fade-in animation delay entirely - update content immediately
            // so the selection still changes correctly, just without the animated transition.
            setSelectedTestimonial(selectedTestimonialImage[0]);
            setFadeIn(true);
            return;
        }

        setFadeIn(false);
        setFadeOutPrevious(true);
        fadeTimeoutRef.current = setTimeout(() => {
            setSelectedTestimonial(selectedTestimonialImage[0]);
            setFadeIn(true);
            fadeTimeoutRef.current = null;
        }, 300); // wait 500ms before updating the image src and fading in again
    }

    const ImageGrid = () => {

        return (
            <div>
                {/* 7.3: converted this grid from a plain <div> of <div>s to a real <ul>/<li>
                    structure with a descriptive `aria-label`, so assistive technology exposes
                    it as a single labelled group of options rather than an unstructured set of
                    generic containers. Deliberately NOT also adding explicit `role="list"`/
                    `role="listitem"`: real <ul>/<li> already computes those roles implicitly,
                    and `eslint-plugin-jsx-a11y`'s `no-redundant-roles` rule (consistent with
                    this codebase's established no-redundant-ARIA convention - see 2.2's
                    `role="banner"` reasoning) flags stacking an explicit role matching the
                    element's implicit one. (Note for future maintainers: some older
                    browser/AT combinations - notably Safari/VoiceOver - are known to drop a
                    <ul>'s implicit `list` role once `list-style: none` is applied via CSS, as
                    it is here in testimonials.scss; if that's confirmed to affect this page's
                    supported AT matrix, the explicit roles should be reinstated as a targeted
                    exception.) */}
                <ul className='img-grid-container' aria-label='Select a testimonial to view'>
                    {testimonialData.map((testimonial, index) => (
                        <li key={index} className='testimonial-list-img-container'>
                            <button type="button" ref={(image) => imageRefSet.current.push(image)} key={index}
                                className='testimonial-list-img-btn'
                                aria-label={`Testimonial of ${testimonial.Name}, ${testimonial.Position}`}
                                aria-pressed={selectedTestimonial.Order === testimonial.Order}
                                onClick={() => handleOnImageClick(testimonial, index, selectedImgRef)}>
                                <img
                                    className={`testimonial-list-img ${selectedTestimonial.Order === testimonial.Order ? 'selected-img' : 'not-selected-img'}`}
                                    src={testimonial.ImagePath} alt="" />
                            </button>
                        </li>
                    ))}
                </ul>
                <hr className='line' ref={lineRef} />
            </div>
        );
    }

    const updateLineLocation = (imagePosition) => {
        const lineElement = lineRef.current;
        const selectedImgElement = imgRect ? imgRect : imagePosition;

        if (lineElement && selectedImgElement) {
            const lineRect = lineElement.getBoundingClientRect();

            const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
            const newLeft = ((selectedImgElement.left - lineRect.width / 2 + selectedImgElement.width / 2) / rootFontSize) - 2.5;
            lineElement.style.left = `${newLeft}rem`;
        }
    }

    useEffect(() => {
        function handleResize() {
            const imagePosition = selectedImgRef?.current?.getBoundingClientRect();
            if (imagePosition) {
                updateLineLocation(imagePosition);
            }
        }

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <div className='testimonial-container' role="region" aria-label="Member Testimonials">
                {/* 7.5 heading-order fix: was <h1>, a second top-level heading sibling to the
                    page's actual <h1> (Main.js hero). Demoted to <h2> to nest correctly under
                    the page's single <h1>, matching Trending Content/Growth Tracking/FAQ. */}
                <h2 className='testimonial-header'>
                    {/* Member Testimonials */}
                    Stories of Success
                </h2>
                {/* 7.4: visually-hidden `aria-live="polite"` region announcing the newly
                    selected testimonial's name/position so a screen reader user is told about
                    the selection change even though the visual update (fade + position
                    indicator move) is otherwise silent to assistive technology. Kept separate
                    from the visible name/position text below (rather than making that text
                    itself the live region) so it announces cleanly regardless of any layout
                    change, and only fires once `selectedTestimonial` has actually resolved.
                    (Deliberately using `aria-live="polite"` alone, not `role="status"` too -
                    `status` already implies `aria-live="polite"`, and per this codebase's
                    established no-redundant-ARIA convention - see 2.2's `role="banner"`
                    reasoning - stacking both would be a redundant declaration.) */}
                <div className='sr-only' aria-live="polite">
                    {selectedTestimonial?.Name &&
                        `Now showing testimonial from ${selectedTestimonial.Name}, ${selectedTestimonial.Position}`}
                </div>
                <div className='selected-member-container'>
                    <div key='selectedDiv' className={`testimonial-selected-container ${fadeIn ? 'fade-in' : 'fade-out'} ${prefersReducedMotion ? 'no-motion' : ''}`}>
                        {/* <img style={{borderRadius: '10px'}} src={previousTestimonialImagePath} className={`selected-testinomial-image ${fadeOutPrevious ? 'fade-out' : 'fade-in'}`} alt={previousSelectedTestimonial.Image}/> */}
                        <img src={testimonialImagePath} className={`selected-testinomial-image`} alt={`Photo of ${selectedTestimonial.Name}, ${selectedTestimonial.Position}`} />
                    </div>
                    <div className='selected-testimonial-info'>
                        <div className='testimonial-body-text'>{selectedTestimonial.Text}</div>
                        <div className='selected-testimonial-location'>
                            <div className='selected-member-info'>
                                {selectedTestimonial.District === '' ?
                                    <>
                                        <div className='member-name'>
                                            {selectedTestimonial.Name}
                                        </div>
                                        <div>
                                            {selectedTestimonial.Position},
                                        </div>
                                        <div>
                                            {selectedTestimonial.School}, {selectedTestimonial.State}
                                        </div>
                                    </>
                                    :
                                    <>
                                        <div className='member-name'>
                                            {selectedTestimonial.Name}
                                        </div>
                                        <div>
                                            {selectedTestimonial.Position}, {selectedTestimonial.School}
                                        </div>
                                        <div>
                                            {selectedTestimonial.District}, {selectedTestimonial.State}
                                        </div>
                                    </>
                                }
                            </div>
                            <img src={stateImagePath} className='selected-testimonial-state-image' alt={`${selectedTestimonial.State} state map`} />
                        </div>
                    </div>
                </div>
                <div className='testimonial-img-container'>
                    {ImageGrid()}
                </div>
            </div>

        </>
    );
}

export default MemberTestimonials;